/**
 * @file Types utilitaires transverses (Vec2, identifiants, Result, DeepReadonly). Depend seulement de shared/bignum (type).
 */
import type { Decimal } from './bignum';

/** Identifiant stable d'entite runtime (entier croissant, jamais reutilise dans une session). */
export type EntityId = number;

/** Identifiant stable d'objet possede par le joueur (arme, familier...) : chaine unique, persistee. */
export type Uid = string;

/** Point/vecteur 2D du plan de simulation. Axe x = droite, axe y = "vers le fond" de l'ecran (nord). */
export interface Vec2 {
  x: number;
  y: number;
}

/** Rend un type profondement lecture-seule (vue UI/render du state du core). Decimal et fonctions sont conserves tels quels. */
export type DeepReadonly<T> = T extends Decimal
  ? T
  : T extends (...args: never[]) => unknown
  ? T
  : T extends (infer U)[]
  ? ReadonlyArray<DeepReadonly<U>>
  : T extends ReadonlyMap<infer K, infer V>
    ? ReadonlyMap<DeepReadonly<K>, DeepReadonly<V>>
    : T extends object
      ? { readonly [K in keyof T]: DeepReadonly<T[K]> }
      : T;

/** Resultat d'operation metier : jamais d'exception pour un refus attendu (pas assez d'argent, etc.). */
export type Result<T = void, E extends string = ErrorCode> =
  | { ok: true; value: T }
  | { ok: false; error: E; detail?: string };

export const ok = <T>(value: T): Result<T, never> => ({ ok: true, value });
export const okVoid: Result<void, never> = { ok: true, value: undefined };
export const fail = <E extends string>(error: E, detail?: string): Result<never, E> =>
  detail === undefined ? { ok: false, error } : { ok: false, error, detail };

/** Codes d'erreur metier (traduits cote UI via la cle `error.<code>`). */
export type ErrorCode =
  | 'notEnoughFunds'
  | 'locked'
  | 'maxLevel'
  | 'invalidTarget'
  | 'inventoryFull'
  | 'cannotMerge'
  | 'notOwned'
  | 'alreadyOwned'
  | 'cooldown'
  | 'noTickets'
  | 'wrongMode'
  | 'unknownId'
  | 'invalidArgument'
  | 'nothingToClaim';
